import * as THREE from 'three';
import type { Game, GameContext, GameMeta, InputState } from '../types';
import { playShoot, playExplosion, playPowerup } from '../../audio/sfx';

type Alien = {
  mesh: THREE.Mesh;
  alive: boolean;
  col: number;
  row: number;
};

type PowerupType = 'rapid' | 'spread' | 'shield';
type Powerup = {
  mesh: THREE.Mesh;
  type: PowerupType;
  vy: number;
};

type Particle = {
  mesh: THREE.Mesh;
  vx: number;
  vy: number;
  life: number;
};

/**
 * Music-reactive Space Drop (Invaders homage).
 * Desktop: arrows/A-D + Space/Z fire
 * Mobile: virtual pad + fire
 */
export class InvadersGame implements Game {
  readonly meta: GameMeta = {
    id: 'invaders',
    title: 'SPACE DROP',
    blurb: 'Shoot the formation. Beat drives the march. Drops tighten the fight.',
    tag: 'ARCADE',
    status: 'playable',
  };

  private ctx!: GameContext;
  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private root = new THREE.Group();
  private ship!: THREE.Mesh;
  private aliens: Alien[] = [];
  private logoTextures: THREE.CanvasTexture[] = [];
  private alienGroup = new THREE.Group();
  private bullets: { mesh: THREE.Mesh; vy: number; fromPlayer: boolean; dx?: number }[] = [];
  private enemyShots: { mesh: THREE.Mesh; vy: number }[] = [];
  private powerups: Powerup[] = [];
  private particles: Particle[] = [];
  private stars!: THREE.Points;

  // Powerup state
  private powerupRapidTimer = 0;
  private powerupSpreadTimer = 0;
  private hasShield = false;
  private shieldMesh?: THREE.Mesh;
  private score = 0;
  private lives = 3;
  private wave = 1;
  private message = 'TAP FIRE / SPACE';
  private messageT = 2.5;
  private shipX = 0;
  private fireCd = 0;
  private invuln = 0;
  private marchDir = 1;
  private marchX = 0;
  private marchCd = 0;
  private formationY = 10;
  private playHalf = 14;
  private disposed = false;
  private lowPower = false;

  async mount(ctx: GameContext): Promise<void> {
    this.ctx = ctx;
    this.lowPower = ctx.quality !== 'high';

    this.renderer = new THREE.WebGLRenderer({
      antialias: !this.lowPower,
      alpha: false,
      powerPreference: this.lowPower ? 'low-power' : 'high-performance',
      canvas: undefined,
    });
    this.renderer.setClearColor(0x030806, 1);
    this.renderer.setPixelRatio(this.pixelRatio());
    ctx.mount.innerHTML = '';
    ctx.mount.appendChild(this.renderer.domElement);
    this.renderer.domElement.className = 'game-canvas';
    this.renderer.domElement.setAttribute('aria-label', 'Space Drop game canvas');

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(50, 1, 0.1, 200);
    this.camera.position.set(0, 0, 42);
    this.camera.lookAt(0, 2, 0);

    this.scene.add(new THREE.AmbientLight(0x88aa99, 0.55));
    const key = new THREE.DirectionalLight(0xa8ffcc, 1.1);
    key.position.set(4, 12, 10);
    this.scene.add(key);

    this.buildStars();
    this.buildShip();
    this.scene.add(this.root);
    this.root.add(this.alienGroup);
    this.spawnWave(1);

    const rect = ctx.mount.getBoundingClientRect();
    this.resize(rect.width || 320, rect.height || 480);
  }

  private pixelRatio(): number {
    const dpr = window.devicePixelRatio || 1;
    if (this.ctx.quality === 'low') return Math.min(1, dpr);
    if (this.ctx.quality === 'medium') return Math.min(1.5, dpr);
    return Math.min(2, dpr);
  }

  private buildStars(): void {
    const count = this.lowPower ? 120 : 280;
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 60;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 40;
      pos[i * 3 + 2] = -8 - Math.random() * 20;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const mat = new THREE.PointsMaterial({
      color: 0xff859b,
      size: this.lowPower ? 0.12 : 0.08,
      transparent: true,
      opacity: 0.7,
      depthWrite: false,
    });
    this.stars = new THREE.Points(geo, mat);
    this.scene.add(this.stars);
  }

  private buildShip(): void {
    const body = new THREE.Mesh(
      new THREE.ConeGeometry(0.7, 1.6, 3),
      new THREE.MeshStandardMaterial({
        color: 0xff4d6d,
        emissive: 0x881133,
        emissiveIntensity: 0.45,
        metalness: 0.2,
        roughness: 0.4,
      }),
    );
    body.rotation.z = Math.PI;
    this.ship = body;
    this.ship.position.set(0, -12, 0);

    const shieldGeo = new THREE.SphereGeometry(1.2, 16, 16);
    const shieldMat = new THREE.MeshBasicMaterial({
      color: 0x4dd4ff,
      transparent: true,
      opacity: 0.4,
      wireframe: true,
    });
    this.shieldMesh = new THREE.Mesh(shieldGeo, shieldMat);
    this.shieldMesh.visible = false;
    this.ship.add(this.shieldMesh);

    this.root.add(this.ship);
  }

  private clearAliens(): void {
    for (const a of this.aliens) {
      this.alienGroup.remove(a.mesh);
      a.mesh.geometry.dispose();
      (a.mesh.material as THREE.Material).dispose();
    }
    this.aliens = [];
  }

  private createLogoTexture(name: string, bgColor: string, fgColor: string): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;

    // Clear background for transparency
    ctx.clearRect(0, 0, 256, 256);

    ctx.fillStyle = bgColor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = 'bold 72px system-ui, sans-serif';

    if (name === 'Bandcamp') {
      // Circle with stylized BC icon
      ctx.beginPath();
      ctx.arc(128, 128, 110, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = fgColor;
      ctx.beginPath();
      ctx.moveTo(80, 160);
      ctx.lineTo(130, 96);
      ctx.lineTo(180, 96);
      ctx.lineTo(130, 160);
      ctx.fill();
    } else if (name === 'Spotify') {
      // Full circle with curved lines
      ctx.beginPath();
      ctx.arc(128, 128, 110, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineWidth = 18;
      ctx.strokeStyle = fgColor;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.arc(128, 165, 65, 1.15 * Math.PI, 1.85 * Math.PI);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(128, 145, 75, 1.1 * Math.PI, 1.9 * Math.PI);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(128, 120, 90, 1.05 * Math.PI, 1.95 * Math.PI);
      ctx.stroke();
    } else if (name === 'Deezer') {
      // Horizontal pill shape with equalizer inside
      ctx.beginPath();
      ctx.roundRect(16, 64, 224, 128, 64);
      ctx.fill();
      ctx.fillStyle = fgColor;
      ctx.fillRect(66, 110, 20, 50);
      ctx.fillRect(102, 90, 20, 70);
      ctx.fillRect(138, 120, 20, 40);
      ctx.fillRect(174, 80, 20, 80);
    } else if (name === 'Apple') {
      // Apple shape with leaf
      ctx.fillStyle = bgColor;
      ctx.beginPath();
      ctx.arc(110, 140, 60, 0, Math.PI * 2);
      ctx.arc(150, 140, 60, 0, Math.PI * 2);
      ctx.fill();
      // base fill to smooth it out
      ctx.beginPath();
      ctx.moveTo(110, 140);
      ctx.lineTo(150, 140);
      ctx.lineTo(130, 200);
      ctx.fill();
      // bite
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(205, 130, 35, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
      // leaf
      ctx.fillStyle = bgColor;
      ctx.beginPath();
      ctx.ellipse(135, 65, 30, 15, -Math.PI / 4, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // UMG / Others: Sharp Square
      ctx.beginPath();
      ctx.rect(32, 32, 192, 192);
      ctx.fill();
      ctx.fillStyle = fgColor;
      ctx.fillText(name.substring(0, 3).toUpperCase(), 128, 128);
    }

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    this.logoTextures.push(tex);
    return tex;
  }

  private getAlienMaterial(row: number, wave: number): THREE.Material {
    const types = [
      { name: 'Bandcamp', bg: '#629aa9', fg: '#ffffff' },
      { name: 'Spotify', bg: '#1ED760', fg: '#ffffff' },
      { name: 'Apple', bg: '#fc3c44', fg: '#ffffff' },
      { name: 'Deezer', bg: '#FEAA2D', fg: '#000000' },
      { name: 'UMG', bg: '#111111', fg: '#ffffff' },
    ];
    // Add randomness based on wave
    const type = types[(row + wave) % types.length];

    // Check if we already created this texture
    let tex = this.logoTextures.find(t => t.name === type.name);
    if (!tex) {
      tex = this.createLogoTexture(type.name, type.bg, type.fg);
      tex.name = type.name;
    }

    const matColor = new THREE.Color(type.bg).lerp(new THREE.Color(0xffffff), 0.2);

    return new THREE.MeshStandardMaterial({
      color: matColor,
      emissive: matColor,
      emissiveIntensity: 0.15,
      roughness: 0.4,
      metalness: 0.1,
      map: tex,
      transparent: true,
    });
  }

  private spawnWave(wave: number): void {
    this.clearAliens();
    this.wave = wave;
    this.marchX = 0;
    this.marchDir = 1;
    this.formationY = 10 - Math.min(3, (wave - 1) * 0.35);
    this.alienGroup.position.set(0, this.formationY, 0);

    const cols = 8;
    const rows = Math.min(5, 3 + Math.floor((wave - 1) / 2));

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        // Flat plane for transparent logos
        const geo = new THREE.PlaneGeometry(1.8, 1.8);
        const mat = this.getAlienMaterial(r, wave);
        const mesh = new THREE.Mesh(geo, mat);

        // Add a slight tilt and varied Z
        mesh.position.set((c - (cols - 1) / 2) * 2.2, -r * 2.0, 0);
        mesh.rotation.x = -0.1;

        this.alienGroup.add(mesh);
        this.aliens.push({ mesh, alive: true, col: c, row: r });
      }
    }
    this.message = `WAVE ${wave}`;
    this.messageT = 1.6;
  }

  private spawnBullet(x: number, y: number, fromPlayer: boolean, dx = 0): void {
    const geo = new THREE.BoxGeometry(0.18, 0.7, 0.18);
    const mat = new THREE.MeshBasicMaterial({
      color: fromPlayer ? (this.powerupRapidTimer > 0 ? 0xffffaa : 0xa8ffcc) : 0xff6688,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, y, 0.2);
    // rotate if spread
    if (dx !== 0) {
      mesh.rotation.z = dx > 0 ? -0.2 : 0.2;
    }
    this.root.add(mesh);
    if (fromPlayer) {
      this.bullets.push({ mesh, vy: 28, fromPlayer: true, dx });
      playShoot();
    } else {
      this.enemyShots.push({ mesh, vy: -14 - this.wave });
    }
  }

  private spawnExplosion(x: number, y: number, color: THREE.Color): void {
    playExplosion();
    const count = this.lowPower ? 6 : 12;
    for (let i = 0; i < count; i++) {
      const geo = new THREE.BoxGeometry(0.2, 0.2, 0.2);
      const mat = new THREE.MeshBasicMaterial({ color });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(x, y, 0.2);
      this.root.add(mesh);
      this.particles.push({
        mesh,
        vx: (Math.random() - 0.5) * 15,
        vy: (Math.random() - 0.5) * 15,
        life: 0.3 + Math.random() * 0.3,
      });
    }
  }

  private spawnPowerup(x: number, y: number): void {
    if (Math.random() > 0.02) return; // 2% drop rate

    const types: PowerupType[] = ['rapid', 'spread', 'shield'];
    const type = types[Math.floor(Math.random() * types.length)];

    let color = 0xffffff;
    if (type === 'rapid') color = 0xffff44;
    else if (type === 'spread') color = 0xff44ff;
    else if (type === 'shield') color = 0x44ffff;

    const geo = new THREE.OctahedronGeometry(0.4);
    const mat = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.5,
      wireframe: true,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, y, 0);
    this.root.add(mesh);
    this.powerups.push({ mesh, type, vy: -5 });
  }

  private aliveAliens(): Alien[] {
    return this.aliens.filter((a) => a.alive);
  }

  private worldAlienPos(a: Alien): THREE.Vector3 {
    const v = new THREE.Vector3();
    a.mesh.getWorldPosition(v);
    return v;
  }

  update(dt: number, input: InputState): void {
    if (this.disposed) return;
    this.maybeRestart(input);
    const bands = this.ctx.getBands();
    const energy = bands.energy;
    const kick = bands.kick;
    const pulse = bands.pulse;
    const beat = bands.beat;

    if (this.messageT > 0) this.messageT -= dt;
    this.fireCd = Math.max(0, this.fireCd - dt);
    this.invuln = Math.max(0, this.invuln - dt);
    this.marchCd = Math.max(0, this.marchCd - dt);

    this.powerupRapidTimer = Math.max(0, this.powerupRapidTimer - dt);
    this.powerupSpreadTimer = Math.max(0, this.powerupSpreadTimer - dt);

    if (this.shieldMesh) {
      this.shieldMesh.visible = this.hasShield;
      if (this.hasShield) {
        this.shieldMesh.rotation.y += dt * 2;
        this.shieldMesh.rotation.z += dt;
      }
    }

    if (this.lives <= 0) {
      // still render scene while dead
      this.renderer.render(this.scene, this.camera);
      return;
    }

    // ship move
    const speed = 18 + energy * 6;
    if (input.left) this.shipX -= speed * dt;
    if (input.right) this.shipX += speed * dt;
    this.shipX = Math.max(-this.playHalf, Math.min(this.playHalf, this.shipX));
    this.ship.position.x = this.shipX;
    this.ship.position.y = -12 + kick * 0.35;
    this.ship.scale.setScalar(1 + kick * 0.08);
    if (this.invuln > 0) {
      this.ship.visible = Math.floor(this.invuln * 12) % 2 === 0;
    } else {
      this.ship.visible = true;
    }

    // fire
    if ((input.firePressed || (input.fire && this.fireCd <= 0)) && this.lives > 0) {
      if (this.fireCd <= 0) {
        this.spawnBullet(this.shipX, -11.2, true);
        if (this.powerupSpreadTimer > 0) {
          this.spawnBullet(this.shipX - 0.4, -11.2, true, -10);
          this.spawnBullet(this.shipX + 0.4, -11.2, true, 10);
        }

        let baseCd = this.powerupRapidTimer > 0 ? 0.12 : 0.28;
        this.fireCd = Math.max(0.08, baseCd - energy * 0.1);
        if (navigator.vibrate) navigator.vibrate(8);
      }
    }

    // Continuous march: interval shrinks with energy/kick; beats add a free step.
    // (Old logic only moved on onset — after energy stabilized, aliens froze.)
    const musicOn = this.ctx.isMusicPlaying();
    const drive = musicOn
      ? 0.35 + energy * 0.55 + kick * 0.25 + bands.mids * 0.1
      : 0.55; // still crawl without music so the game never deadlocks
    const marchInterval = Math.max(0.14, 0.62 - drive * 0.38 - this.wave * 0.012);
    const wantBeatStep = beat > 0.1 || pulse > 0.16;

    if (this.aliveAliens().length && (this.marchCd <= 0 || wantBeatStep)) {
      if (this.marchCd > 0 && wantBeatStep) {
        // beat bonus: only if we didn't just step
        if (this.marchCd > marchInterval * 0.55) {
          /* skip double-step on same frame cluster */
        } else {
          this.doMarchStep(energy, kick, bands.mids);
          this.marchCd = marchInterval;
        }
      } else if (this.marchCd <= 0) {
        this.doMarchStep(energy, kick, bands.mids);
        this.marchCd = marchInterval;
      }
    }

    this.alienGroup.position.x = this.marchX;
    this.alienGroup.position.y = this.formationY;

    // alien pulse scale
    for (const a of this.aliveAliens()) {
      a.mesh.scale.setScalar(1 + kick * 0.12 + bands.mids * 0.05);
    }

    // particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= dt;
      p.mesh.position.x += p.vx * dt;
      p.mesh.position.y += p.vy * dt;
      p.mesh.rotation.z += dt * 5;
      if (p.life <= 0) {
        this.root.remove(p.mesh);
        p.mesh.geometry.dispose();
        (p.mesh.material as THREE.Material).dispose();
        this.particles.splice(i, 1);
      }
    }

    // powerups
    for (let i = this.powerups.length - 1; i >= 0; i--) {
      const p = this.powerups[i];
      p.mesh.position.y += p.vy * dt;
      p.mesh.rotation.y += dt * 2;
      p.mesh.rotation.x += dt;

      if (p.mesh.position.y < -16) {
        this.root.remove(p.mesh);
        p.mesh.geometry.dispose();
        (p.mesh.material as THREE.Material).dispose();
        this.powerups.splice(i, 1);
        continue;
      }

      // collision with player
      if (
        this.lives > 0 &&
        Math.abs(p.mesh.position.x - this.shipX) < 1.2 &&
        Math.abs(p.mesh.position.y - this.ship.position.y) < 1.2
      ) {
        playPowerup();
        if (p.type === 'rapid') this.powerupRapidTimer += 5;
        else if (p.type === 'spread') this.powerupSpreadTimer += 5;
        else if (p.type === 'shield') this.hasShield = true;

        this.root.remove(p.mesh);
        p.mesh.geometry.dispose();
        (p.mesh.material as THREE.Material).dispose();
        this.powerups.splice(i, 1);
        this.score += 50;
      }
    }

    // player bullets
    for (const b of this.bullets) {
      b.mesh.position.y += b.vy * dt;
      if (b.dx) b.mesh.position.x += b.dx * dt;
    }
    this.bullets = this.bullets.filter((b) => {
      if (b.mesh.position.y > 18) {
        this.root.remove(b.mesh);
        b.mesh.geometry.dispose();
        (b.mesh.material as THREE.Material).dispose();
        return false;
      }
      for (const a of this.aliens) {
        if (!a.alive) continue;
        const p = this.worldAlienPos(a);
        if (
          Math.abs(b.mesh.position.x - p.x) < 0.85 &&
          Math.abs(b.mesh.position.y - p.y) < 0.7
        ) {
          a.alive = false;
          a.mesh.visible = false;
          this.score += 10 + Math.floor(energy * 20);

          this.spawnExplosion(p.x, p.y, (a.mesh.material as THREE.MeshStandardMaterial).color);
          this.spawnPowerup(p.x, p.y);

          this.root.remove(b.mesh);
          b.mesh.geometry.dispose();
          (b.mesh.material as THREE.Material).dispose();
          return false;
        }
      }
      return true;
    });

    // enemy shots
    for (const s of this.enemyShots) {
      s.mesh.position.y += s.vy * dt * (1 + energy * 0.35);
    }
    this.enemyShots = this.enemyShots.filter((s) => {
      if (s.mesh.position.y < -16) {
        this.root.remove(s.mesh);
        s.mesh.geometry.dispose();
        (s.mesh.material as THREE.Material).dispose();
        return false;
      }
      if (
        this.lives > 0 &&
        this.invuln <= 0 &&
        Math.abs(s.mesh.position.x - this.shipX) < 0.9 &&
        Math.abs(s.mesh.position.y - this.ship.position.y) < 0.9
      ) {
        this.hitPlayer();
        this.root.remove(s.mesh);
        s.mesh.geometry.dispose();
        (s.mesh.material as THREE.Material).dispose();
        return false;
      }
      return true;
    });

    // aliens reach bottom
    for (const a of this.aliveAliens()) {
      const p = this.worldAlienPos(a);
      if (p.y < -11 && this.invuln <= 0 && this.lives > 0) {
        this.hitPlayer();
        break;
      }
    }

    // wave clear
    if (this.aliveAliens().length === 0 && this.lives > 0) {
      this.score += 100 * this.wave;
      this.spawnWave(this.wave + 1);
    }

    // stars drift with energy
    this.stars.rotation.z += dt * (0.02 + energy * 0.05);
    this.stars.position.y = Math.sin(performance.now() * 0.0004) * energy;

    this.camera.position.x = this.shipX * 0.04;
    this.camera.position.y = kick * 0.3;
    this.renderer.render(this.scene, this.camera);
  }

  private doMarchStep(energy: number, kick: number, mids: number): void {
    const step = 0.45 + energy * 0.5 + this.wave * 0.04;
    this.marchX += this.marchDir * step;

    let minX = Infinity;
    let maxX = -Infinity;
    for (const a of this.aliveAliens()) {
      const wx = this.marchX + a.mesh.position.x;
      minX = Math.min(minX, wx);
      maxX = Math.max(maxX, wx);
    }
    if (maxX > this.playHalf) {
      this.marchDir = -1;
      this.marchX -= step;
      this.formationY -= 0.5 + kick * 0.3;
    } else if (minX < -this.playHalf) {
      this.marchDir = 1;
      this.marchX += step;
      this.formationY -= 0.5 + kick * 0.3;
    }

    // enemy fire chance per step
    if (Math.random() < 0.12 + energy * 0.22 + mids * 0.08) {
      const pool = this.aliveAliens();
      const shooter = pool[Math.floor(Math.random() * pool.length)];
      if (shooter) {
        const p = this.worldAlienPos(shooter);
        this.spawnBullet(p.x, p.y - 0.6, false);
      }
    }
  }

  private hitPlayer(): void {
    if (this.hasShield) {
      this.hasShield = false;
      this.invuln = 1.6;
      playExplosion();
      this.message = 'SHIELD BROKEN';
      this.messageT = 1.2;
      return;
    }

    this.lives -= 1;
    this.invuln = 1.6;
    this.powerupRapidTimer = 0;
    this.powerupSpreadTimer = 0;
    playExplosion();

    if (navigator.vibrate) navigator.vibrate([20, 40, 20]);
    if (this.lives <= 0) {
      this.message = 'GAME OVER — FIRE TO RETRY';
      this.messageT = 99;
    } else {
      this.message = 'HIT';
      this.messageT = 0.8;
    }
  }

  /** Retry after game over when fire pressed */
  maybeRestart(input: InputState): void {
    if (this.lives <= 0 && input.firePressed) {
      this.score = 0;
      this.lives = 3;
      this.spawnWave(1);
    }
  }

  resize(w: number, h: number): void {
    if (!this.renderer) return;
    const width = Math.max(1, w);
    const height = Math.max(1, h);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    // keep ship framing on tall phones
    this.camera.position.z = width / height < 0.7 ? 48 : 42;
  }

  getHud() {
    return {
      score: this.score,
      lives: Math.max(0, this.lives),
      wave: this.wave,
      message: this.messageT > 0 ? this.message : undefined,
    };
  }

  dispose(): void {
    this.disposed = true;
    this.clearAliens();
    for (const tex of this.logoTextures) {
      tex.dispose();
    }
    this.logoTextures = [];
    for (const b of this.bullets) {
      b.mesh.geometry.dispose();
      (b.mesh.material as THREE.Material).dispose();
    }
    for (const s of this.enemyShots) {
      s.mesh.geometry.dispose();
      (s.mesh.material as THREE.Material).dispose();
    }
    for (const p of this.powerups) {
      p.mesh.geometry.dispose();
      (p.mesh.material as THREE.Material).dispose();
    }
    for (const p of this.particles) {
      p.mesh.geometry.dispose();
      (p.mesh.material as THREE.Material).dispose();
    }
    if (this.shieldMesh) {
      this.shieldMesh.geometry.dispose();
      (this.shieldMesh.material as THREE.Material).dispose();
    }
    this.bullets = [];
    this.enemyShots = [];
    this.powerups = [];
    this.particles = [];
    this.renderer?.dispose();
    if (this.renderer?.domElement.parentElement) {
      this.renderer.domElement.parentElement.removeChild(this.renderer.domElement);
    }
  }
}
